// Channels — the chat shell. Two-pane on desktop (channel list + conversation),
// single-pane with back navigation on mobile. Loads channels + history over REST
// and streams live updates via the ChatHub (useChat).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { ChannelsApi, CommandsApi, InboxApi, MeetingsApi, PinsApi, PrefsApi, SavedApi, ScheduledApi, ThreadsApi, UsersApi } from "../hooks/useApi.js";
import WaitingPanel from "../components/WaitingPanel.jsx";
import SignupsPanel from "../components/SignupsPanel.jsx";
import { whenLabel } from "../lib/when.js";
import { PollModal, PollPin, parsePollMeta } from "../components/Poll.jsx";
import { IdeaModal, IdeaPanel } from "../components/Idea.jsx";
import { useChat } from "../hooks/useChat.js";
import { usePush } from "../hooks/usePush.js";
import { setTabUnread } from "../lib/tabBadge.js";
import ChannelList from "../components/ChannelList.jsx";
import DreamTeamPin from "../components/DreamTeamPin.jsx";
import DreamTeamLogo from "../components/DreamTeamLogo.jsx";
import ReportingPin, { ReportingLogo } from "../components/ReportingPin.jsx";
import MessageList from "../components/MessageList.jsx";
import Composer from "../components/Composer.jsx";
import MeetingPin from "../components/MeetingPin.jsx";
import { playNotification } from "../notificationSound.js";
import CyberRain from "../components/CyberRain.jsx";
import AmbientScene from "../components/AmbientScene.jsx";
import CyberStatus from "../components/CyberStatus.jsx";
import NeuralBoot from "../components/NeuralBoot.jsx";
import NeuralConnecting from "../components/NeuralConnecting.jsx";
import InstallPrompt from "../components/InstallPrompt.jsx";
import Icon from "../components/Icon.jsx";
import Welcome from "../components/Welcome.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { playNudgeBuzz } from "../lib/voicefx.js";
import { lazyPanel, prefetchPanels } from "../lib/lazyPanel.jsx";
// Rarely-used panels and modals: loaded on demand (prefetched when idle) to keep the first load small.
const ChannelSettings = lazyPanel(() => import("../components/ChannelSettings.jsx"));
const ProfileSettings = lazyPanel(() => import("../components/ProfileSettings.jsx"));
const TicketDrawer = lazyPanel(() => import("../components/TicketDrawer.jsx"));
const SearchDrawer = lazyPanel(() => import("../components/SearchDrawer.jsx"));
const ScheduleMeetingModal = lazyPanel(() => import("../components/ScheduleMeetingModal.jsx"));
const MeetingPanel = lazyPanel(() => import("../components/MeetingPanel.jsx"));
const ThemePicker = lazyPanel(() => import("../components/ThemePicker.jsx"));
const NotificationSettings = lazyPanel(() => import("../components/NotificationSettings.jsx"));
const MeetingsAgenda = lazyPanel(() => import("../components/MeetingsAgenda.jsx"));
const SummaryModal = lazyPanel(() => import("../components/SummaryModal.jsx"));
const CommandModal = lazyPanel(() => import("../components/CommandModal.jsx"));
const CreateMenuModal = lazyPanel(() => import("../components/CreateMenuModal.jsx"));
const Tutorial = lazyPanel(() => import("../components/Tutorial.jsx"));
const TicketDetailModal = lazyPanel(() => import("../components/TicketDetailModal.jsx"));
const SignoffPdfModal = lazyPanel(() => import("../components/SignoffPdfModal.jsx"));
const CampaignBuilder = lazyPanel(() => import("../components/CampaignBuilder.jsx"));
const CampaignPdfModal = lazyPanel(() => import("../components/CampaignPdfModal.jsx"));
const ReportModal = lazyPanel(() => import("../components/ReportModal.jsx"));
const NewProductModal = lazyPanel(() => import("../components/NewProductModal.jsx"));
const NewChannelModal = lazyPanel(() => import("../components/NewChannelModal.jsx"));
const CreateStoriesModal = lazyPanel(() => import("../components/CreateStoriesModal.jsx"));
const ForwardModal = lazyPanel(() => import("../components/ForwardModal.jsx"));
const AdReportModal = lazyPanel(() => import("../components/AdReportModal.jsx"));
const PortfolioTasksModal = lazyPanel(() => import("../components/PortfolioTasksModal.jsx"));
const BirthdayModal = lazyPanel(() => import("../components/BirthdayModal.jsx"));
const SavedPanel = lazyPanel(() => import("../components/SavedPanel.jsx"));
const MediaPanel = lazyPanel(() => import("../components/MediaPanel.jsx"));
const PinnedPanel = lazyPanel(() => import("../components/PinnedPanel.jsx"));
const ApprovalsPanel = lazyPanel(() => import("../components/ApprovalsPanel.jsx"));
const RecapModal = lazyPanel(() => import("../components/RecapModal.jsx"));
const ThreadPanel = lazyPanel(() => import("../components/ThreadPanel.jsx"));

// Sidebar last-message preview (mirrors the server's ChannelService.Preview).
function previewOf(m, myId) {
  const body = m.messageType === 1 ? "🎤 Voice note"
    : m.messageType === 2 ? "🖼 Image"
    : m.messageType === 3 ? "🎬 Video"
    : m.messageType === 10 ? `📎 ${m.content || "File"}`
    : (m.content || "").replace(/\n/g, " ");
  const short = body.length > 60 ? body.slice(0, 57) + "…" : body;
  if (m.messageType === 0) {
    const who = m.senderUserId === myId ? "You" : (m.senderName || "").split(" ")[0];
    return `${who}: ${short}`;
  }
  return short;
}

// One-line snippet of a message for the reply banner / reference.
function snippetOf(m) {
  const b = m.messageType === 1 ? "🎤 Voice note" : m.messageType === 2 ? "🖼 Image" : m.messageType === 3 ? "🎬 Video"
    : (m.content || "").replace(/\n/g, " ").trim();
  return b.length > 80 ? b.slice(0, 80) + "…" : b;
}

// Apply a live ReactionChanged delta {messageId, emoji, userId, added} to a message list.
function applyReactionDelta(list, e, myId) {
  return list.map((m) => {
    if (m.id !== e.messageId) return m;
    const reactions = [...(m.reactions || [])];
    const idx = reactions.findIndex((r) => r.emoji === e.emoji);
    const mineDelta = e.userId === myId;
    if (e.added) {
      if (idx >= 0) {
        const userIds = [...(reactions[idx].userIds || [])];
        if (!userIds.includes(e.userId)) userIds.push(e.userId);
        reactions[idx] = { ...reactions[idx], count: reactions[idx].count + 1, mine: reactions[idx].mine || mineDelta, userIds };
      } else reactions.push({ emoji: e.emoji, count: 1, mine: mineDelta, userIds: [e.userId] });
    } else if (idx >= 0) {
      const count = reactions[idx].count - 1;
      if (count <= 0) reactions.splice(idx, 1);
      else {
        const userIds = (reactions[idx].userIds || []).filter((id) => id !== e.userId);
        reactions[idx] = { ...reactions[idx], count, mine: mineDelta ? false : reactions[idx].mine, userIds };
      }
    }
    return { ...m, reactions };
  });
}

const PAGE = 40; // messages per history page (matches the server's default limit)

// Merge the latest server page into what's already shown, instead of replacing it. Inside the page's id range
// the server copy wins (edits, deletions, reactions, thread counts); older pages the user scrolled up to stay;
// client-only items — private command cards (negative ids) and optimistic loaders (string ids) — are kept.
const isServerMsg = (m) => typeof m.id === "number" && m.id > 0;
function mergeLatest(prev, page) {
  const local = prev.filter((m) => !isServerMsg(m));
  if (!page.length) return { list: local, keptOlder: false };
  const minId = page[0].id;
  const older = prev.filter((m) => isServerMsg(m) && m.id < minId);
  return { list: [...older, ...page, ...local], keptOlder: older.length > 0 };
}
const CACHE_CHANNELS = 15; // channels whose last-seen messages are kept for an instant re-open

// Local-only demo cards the tutorial seeds into the message list (never sent / persisted) so the
// report + sign-off steps have something real to spotlight. Ids are far out of the real range.
const TOUR_NOW = new Date().toISOString();
const TOUR_DEMO_CARDS = [
  {
    id: 900000001, senderName: "TMCHub", messageType: 5, createdAt: TOUR_NOW,
    metadataJson: JSON.stringify({
      kind: "report", title: "Weekly delivery report", health: "On track",
      completed: 12, inProgress: 5, resolved: 8, open: 3,
      summary: "Great momentum this week — most in-progress items are on track to close by Friday.",
    }),
  },
  {
    id: 900000002, senderName: "TMCHub", messageType: 6, createdAt: TOUR_NOW,
    metadataJson: JSON.stringify({
      demo: true, docId: -1, title: "Brand Refresh", version: "v2.4 · Release sign-off",
      status: "PendingSignoff", pdfUrl: null,
      approvers: [
        { userId: -101, name: "Alex Client", signed: true, signedAt: TOUR_NOW },
        { userId: -102, name: "Sam Client", signed: false },
      ],
    }),
  },
];

export default function Channels() {
  const { user, signOut } = useAuth();
  const push = usePush();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  // Post-sign-in "ESTABLISHING NEURAL LINK" boot loader (cyberpunk only; flag set by Login).
  const [booting, setBooting] = useState(() => { try { return sessionStorage.getItem("tmc.boot") === "1"; } catch { return false; } });
  const [connecting, setConnecting] = useState(false); // channel-switch "re-routing" overlay
  const [pendingName, setPendingName] = useState("");
  const [hideStatus, setHideStatus] = useState(false); // per-user: hide status-update cards in the active channel
  const [summaryDlg, setSummaryDlg] = useState(null); // { channelName, loading, error, summary, count } | null
  const [messages, setMessages] = useState([]);
  const [typingName, setTypingName] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [settingsMenu, setSettingsMenu] = useState(false); // sidebar-header gear dropdown
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [headerMenu, setHeaderMenu] = useState(null); // channel-header ⋯ menu position { top, right } or null
  const headerBtnRef = useRef(null);
  const openHeaderMenu = () => {
    if (headerMenu) { setHeaderMenu(null); return; }
    const r = headerBtnRef.current?.getBoundingClientRect();
    if (r) setHeaderMenu({ top: r.bottom + 6, right: window.innerWidth - r.right });
  };
  const [editSignal, setEditSignal] = useState(null); // { id, nonce } — "edit my last message" request
  const [showTickets, setShowTickets] = useState(false);
  const [search, setSearch] = useState(null); // { prefill } when the cross-channel search panel is open
  const openSearch = useCallback((prefill = "") => setSearch({ prefill }), []);
  const [jumpTo, setJumpTo] = useState(null); // { channelId, messageId } → scroll to that message after opening
  const [showSchedule, setShowSchedule] = useState(false); // schedule-meeting modal
  const [activeMeeting, setActiveMeeting] = useState(null); // { code } → inline MeetingPanel
  const [showSummarize, setShowSummarize] = useState(false); // staff /summarize period picker
  const [pollModal, setPollModal] = useState(null); // { initialQuestion } when the /poll dialog is open
  const [ideaModal, setIdeaModal] = useState(null); // { initialTitle } when the /idea dialog is open
  const [openIdea, setOpenIdea] = useState(null);   // the idea message whose discussion panel is open
  const [pinnedMsgs, setPinnedMsgs] = useState([]); // pinned messages — polls (top-right rail) + chat pins (⋯ menu panel)
  const [showBday, setShowBday] = useState(false); // staff one-time "when's your birthday?" ask
  const [bdayNames, setBdayNames] = useState([]); // staff with a birthday today (Dream Team banner)
  const [showAppearance, setShowAppearance] = useState(false); // theme picker modal (all users)
  const [cmdModal, setCmdModal] = useState(null); // { kind: 'bug'|'feature'|'story', initialTitle } | null
  const [createMenu, setCreateMenu] = useState(null); // { initialTitle } when the /create chooser is open
  const [confirmDelete, setConfirmDelete] = useState(null); // message pending delete confirmation
  const [confirmBroadcast, setConfirmBroadcast] = useState(null); // { text, mentionedIds, msg } pending /broadcast
  const [editMsg, setEditMsg] = useState(null); // message being edited in the dialog
  const [editDraft, setEditDraft] = useState("");
  const [storiesModal, setStoriesModal] = useState(null); // meeting-notes message → Create stories dialog
  // Threads: the open thread's root id + its messages (root first). The ref lets SignalR handlers route live replies.
  const [threadRootId, setThreadRootId] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const threadRootRef = useRef(null);
  // "New messages" line: the first message that was unread when the channel was opened.
  const [newMarkerId, setNewMarkerId] = useState(null);
  // Saved messages: messageId → pending reminder ISO (or null). Loaded once; kept in step by save/unsave.
  const [savedMap, setSavedMap] = useState(() => new Map());
  const [showSaved, setShowSaved] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [showPinned, setShowPinned] = useState(false);
  // My pending scheduled messages in the open channel.
  const [scheduled, setScheduled] = useState([]);
  const [schedOpen, setSchedOpen] = useState(false);
  // "Waiting on you" — things across my channels that need my action; refreshed every 5 minutes.
  const [approvals, setApprovals] = useState([]);
  const [showApprovals, setShowApprovals] = useState(false);
  const [recapFor, setRecapFor] = useState(null); // meeting-notes message → client recap dialog
  // Who's on approved leave today: userId → "YYYY-MM-DD" (until). Refreshed hourly.
  const [awayById, setAwayById] = useState({});
  const [forwardMsg, setForwardMsg] = useState(null); // message pending a forward-to-channels pick
  const [meetLiveCounts, setMeetLiveCounts] = useState({}); // meeting code → live headcount (broadcasts)
  const [pinRefresh, setPinRefresh] = useState(0); // bumped on MeetingUpdated → pin refetches now
  const [mentionables, setMentionables] = useState([]);
  const [toast, setToast] = useState(null); // { title, body } | null
  const [jumpscare, setJumpscare] = useState(false); // /jump overlay visible
  const jumpTimer = useRef(null);
  const jumpSound = useRef(null);
  // Pre-warm the jumpscare assets — a scare that pauses to download isn't a scare.
  useEffect(() => {
    const i = new Image(); i.src = "/fx/grudge.png";
    const a = new Audio("/fx/grudge.mp3"); a.preload = "auto"; jumpSound.current = a;
  }, []);
  const [lightbox, setLightbox] = useState(null); // image url | null
  const [openTicket, setOpenTicket] = useState(null); // card metadata of a tapped event card | null
  const [openPdf, setOpenPdf] = useState(null); // sign-off doc whose PDF modal is open | null
  const [campaign, setCampaign] = useState(null); // { channel, reviseBriefId? } when the campaign builder is open
  const [openBriefPdf, setOpenBriefPdf] = useState(null); // { briefId, title, version } | null
  const [openReport, setOpenReport] = useState(null); // @report card metadata whose full-report modal is open | null
  const [hasMore, setHasMore] = useState(false); // older messages available to lazy-load
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [deletedRefs, setDeletedRefs] = useState(() => new Set()); // "issue:{id}"/"task:{id}" deleted in TMCHub (live)
  const [reply, setReply] = useState(null); // armed reply target { id, senderName, senderUserId, snippet }
  const [ticketables, setTicketables] = useState([]); // open tickets for the "#" picker (from @tickets endpoint)
  const [ticketTags, setTicketTags] = useState([]); // tickets armed to tag on the next message [{ id, issueId, taskId, name, isBug }]
  const [channelTags, setChannelTags] = useState([]); // Dream Team "#": channels referenced on the next message [{ id, name }]
  const [selectionMode, setSelectionMode] = useState(false); // staff multi-select mode
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // selected message ids
  const [planBusy, setPlanBusy] = useState(false); // "Create Tickets" in flight
  const [statusPick, setStatusPick] = useState(null); // Dream Team /status product picker: { fromId, selected:Set } | null
  const [portfolioDrill, setPortfolioDrill] = useState(null); // { title, clientId?, projectId?, metric, days? } | null
  const [notifyNudge, setNotifyNudge] = useState(false); // "turn on notifications" prompt shown on open when they're off
  const [newProduct, setNewProduct] = useState(null); // Dream Team /newproduct dialog: { open:true } | null
  const [newChannel, setNewChannel] = useState(false); // staff "+ New channel" dialog (a channel with no TMCHub product)
  const [adReportPick, setAdReportPick] = useState(false); // Reporting: the /adreport period picker is open
  const [newChannelId, setNewChannelId] = useState(null); // just-created channel → floats to list top + entrance animation
  const [tutorialOpen, setTutorialOpen] = useState(false); // interactive onboarding tour
  const [demoMenu, setDemoMenu] = useState(null); // tour: force the composer's slash/@ menu open ('slash'|'mention'|null)
  const [tourCards, setTourCards] = useState([]); // tour: local-only demo report/sign-off cards
  const newChannelTimer = useRef(null);
  const toastTimer = useRef(null);
  const MAX_SELECT = 50;

  const reloadChannels = useCallback(
    () => ChannelsApi.list().then((list) => {
      setChannels(list);
      // The open conversation's header reads `active`, not the list — refresh its metadata too, so a
      // rename in Channel settings shows immediately rather than on the next open.
      setActive((a) => {
        const fresh = a && list.find((c) => c.id === a.id);
        return fresh ? { ...a, name: fresh.name, iconUrl: fresh.iconUrl, description: fresh.description } : a;
      });
    }).catch(() => {}),
    [],
  );

  // Start an instant huddle in the active channel and open it inline. Camera is
  // off to start with — you turn it on in the call (so one button, not voice/video).
  async function startHuddle() {
    if (!active) return;
    try {
      const meeting = await MeetingsApi.create({ channelId: active.id, isVideo: true });
      setActiveMeeting({ code: meeting.code });
    } catch {
      setToast({ title: "Meeting", body: "Could not start the huddle." });
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 6000);
    }
  }

  const activeIdRef = useRef(null);
  activeIdRef.current = active?.id ?? null;

  // The channel's currently-live HUDDLE (instant meeting, no scheduled time, status Live),
  // read from its meeting card in the loaded messages — kept live by the MeetingUpdated /
  // MeetingParticipants handlers. Drives the pinned "Join huddle" pill so people can hop off
  // and back onto the one huddle without scrolling to find the card.
  const liveHuddle = useMemo(() => {
    for (const m of messages) {
      if (m.messageType !== 9 || !m.metadataJson) continue;
      try {
        const mt = JSON.parse(m.metadataJson).meeting;
        if (mt && !mt.scheduledAt && mt.status === 1) return mt;
      } catch { /* ignore */ }
    }
    return null;
  }, [messages]);
  // Last channel we opened — survives mobile "back to list" (where active → null), so switching
  // to a different channel still counts as a switch (drives the "re-routing" jack-in overlay).
  const lastChannelIdRef = useRef(null);
  const messagesRef = useRef([]);
  messagesRef.current = messages;
  // Last-seen messages per channel (server messages only), so re-opening a channel shows them instantly while
  // the fresh page loads. Small LRU.
  const msgCache = useRef(new Map());
  useEffect(() => {
    const id = active?.id;
    if (!id || !messages.length) return;
    const c = msgCache.current;
    c.delete(id);
    c.set(id, { messages: messages.filter(isServerMsg), hasMore });
    while (c.size > CACHE_CHANNELS) c.delete(c.keys().next().value);
  }, [messages, hasMore, active?.id]);
  const loadingOlderRef = useRef(false);
  const typingTimer = useRef(null);
  const typingClear = useRef(null);

  // --- live updates -------------------------------------------------------
  const { state, send, edit, remove, forward, toggleReaction, typing, markRead, joinChannel, leaveChannel } = useChat({
    onReceive: (m) => {
      if (m.threadRootId) {
        // A thread reply: bump the root's "N replies" in the stream, and append it if that thread is open.
        if (m.channelId === activeIdRef.current) {
          setMessages((prev) => prev.map((x) => (x.id === m.threadRootId
            ? { ...x, threadReplyCount: (x.threadReplyCount || 0) + 1, threadLastAt: m.createdAt } : x)));
          if (threadRootRef.current === m.threadRootId)
            setThreadMsgs((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
        }
        return;
      }
      const isActive = m.channelId === activeIdRef.current;
      const mine = m.senderUserId === user?.userId;
      if (isActive) {
        // A CommandResult arriving (the summary or its error) clears the optimistic /summarize loader.
        // De-dupe by id so a card we already rendered optimistically (e.g. /bug) isn't doubled.
        setMessages((prev) => {
          if (m.messageType === 5) {
            // A /summarize result morphs the loader IN PLACE: reuse the loader's id (React key) so
            // the SummaryCard transitions loading→done rather than unmount/remount.
            let k = ""; try { k = JSON.parse(m.metadataJson || "{}").kind; } catch { /* ignore */ }
            if (k === "summary" || k === "summary-error") {
              const i = prev.findIndex((x) => String(x.id).startsWith("sum-"));
              if (i >= 0) { const next = prev.slice(); next[i] = { ...m, id: prev[i].id }; return next; }
            }
            // Same for a /ask answer (or its error) and its "q-" loader.
            if (k === "question" || k === "question-error") {
              const i = prev.findIndex((x) => String(x.id).startsWith("q-"));
              if (i >= 0) { const next = prev.slice(); next[i] = { ...m, id: prev[i].id }; return next; }
            }
            const clearsAdr = k === "adreport" || k === "adreport-error";
            const base = prev.filter((x) => !String(x.id).startsWith("sum-") && !(clearsAdr && String(x.id).startsWith("adr-")));
            return base.some((x) => x.id === m.id) ? base : [...base, m];
          }
          return prev.some((x) => x.id === m.id) ? prev : [...prev, m];
        });
        markRead(m.channelId, m.id); // viewing it → keep it read
        // A pinned message arriving (a fresh /poll) joins the pins immediately.
        if (m.pinnedAt) setPinnedMsgs((prev) => (prev.some((x) => x.id === m.id) ? prev : [m, ...prev]));
      }
      // A broadcast lands in EVERY channel at once; bumping lastMessageAt would re-sort the whole
      // sidebar (recency order) and make the rows jump around. Keep the row's sort time unchanged
      // for broadcasts — still refresh the preview + unread so the announcement shows in place.
      let kind = ""; try { kind = JSON.parse(m.metadataJson || "{}").kind; } catch { /* ignore */ }
      const isBroadcast = kind === "broadcast";
      // Update the sidebar row live: preview + time, and an unread bump when it's
      // not the channel you're looking at (and not your own message).
      setChannels((prev) => prev.map((c) => c.id !== m.channelId ? c : {
        ...c,
        lastMessagePreview: previewOf(m, user?.userId),
        lastMessageAt: isBroadcast ? c.lastMessageAt : m.createdAt,
        unreadCount: isActive || mine ? (c.unreadCount ?? 0) : (c.unreadCount ?? 0) + 1,
      }));
      // Ping for a message landing in another channel — skip your own, muted channels, meeting
      // cards, and @mentions of you (those get the mention sound via onMentioned, no double-up).
      if (m.messageType !== 9 && !isActive && !mine) {
        let mentionsMe = false;
        try { const md = JSON.parse(m.metadataJson || "{}"); mentionsMe = Array.isArray(md.mentions) && md.mentions.includes(user?.userId); } catch { /* no meta */ }
        if (!mentionsMe && !channels.find((c) => c.id === m.channelId)?.isMuted) playNotification("message");
      }
    },
    onEdited: (m) => {
      if (m.channelId === activeIdRef.current) {
        // The broadcast DTO is bare (no reactions / reply quote / thread summary) — keep what we had.
        const merge = (x) => ({ ...m, reactions: m.reactions?.length ? m.reactions : x.reactions,
          replyTo: m.replyTo ?? x.replyTo, threadReplyCount: x.threadReplyCount, threadLastAt: x.threadLastAt });
        setMessages((prev) => prev.map((x) => (x.id === m.id ? merge(x) : x)));
        setThreadMsgs((prev) => prev.map((x) => (x.id === m.id ? merge(x) : x)));
        // Poll votes/closures and pin/unpin arrive as edits — keep the pins' copy current even
        // when the message itself is outside the loaded history window (render drops closed/unpinned
        // ones); a message someone just pinned joins them here.
        setPinnedMsgs((prev) => {
          const i = prev.findIndex((x) => x.id === m.id);
          if (i >= 0) { const next = prev.slice(); next[i] = m; return next; }
          return m.pinnedAt ? [m, ...prev] : prev;
        });
      }
    },
    onDeleted: (m) => {
      if (m.channelId === activeIdRef.current) {
        if (m.threadRootId) {
          setMessages((prev) => prev.map((x) => (x.id === m.threadRootId
            ? { ...x, threadReplyCount: Math.max(0, (x.threadReplyCount || 1) - 1) } : x)));
        } else {
          setMessages((prev) => prev.map((x) => (x.id === m.id ? m : x)));
        }
        setThreadMsgs((prev) => prev.map((x) => (x.id === m.id ? m : x)));
        setPinnedMsgs((prev) => prev.filter((x) => x.id !== m.id));
      }
    },
    // A newer card replaced older ones (a ticket's status updates collapse to a single card) — drop
    // the superseded ids from the view. Like a move, this is not a delete: no tombstone, and the
    // transitions they showed live on in the new card's history (the detail modal renders them).
    onSuperseded: (e) => {
      if (e.channelId !== activeIdRef.current) return;
      const gone = new Set(e.ids || []);
      if (!gone.size) return;
      setMessages((prev) => prev.filter((x) => !gone.has(x.id)));
    },
    // A ticket card was moved to another product's channel — drop it from the OLD channel's view
    // instantly (it's a relocation, not a delete, so remove it rather than show a tombstone).
    onMoved: (e) => {
      if (e.fromChannelId === activeIdRef.current) {
        setMessages((prev) => prev.filter((x) => x.id !== e.id));
        setPinnedMsgs((prev) => prev.filter((x) => x.id !== e.id));
      }
      // Reconcile the SIDEBAR from the server: the moved card was likely the old channel's last
      // message (so its preview/unread are now stale) and the new channel gained one. Re-pull the
      // authoritative summaries and merge preview/unread/time for every row (moves are rare).
      ChannelsApi.list().then((list) => {
        if (!Array.isArray(list)) return;
        const byId = Object.fromEntries(list.map((c) => [c.id, c]));
        setChannels((prev) => prev.map((c) => byId[c.id]
          ? { ...c, lastMessagePreview: byId[c.id].lastMessagePreview, lastMessageAt: byId[c.id].lastMessageAt, unreadCount: byId[c.id].unreadCount }
          : c));
      }).catch(() => { /* sidebar just updates on its next event */ });
    },
    onTyping: (t) => {
      if (t.channelId !== activeIdRef.current) return;
      setTypingName(t.name);
      clearTimeout(typingClear.current);
      typingClear.current = setTimeout(() => setTypingName(null), 2500);
    },
    onMembership: (e) => {
      // Staff added/removed me from a channel — update my groups + channel list live.
      if (e.action === "added") joinChannel(e.channelId);
      if (e.action === "removed") {
        leaveChannel(e.channelId);
        if (activeIdRef.current === e.channelId) setActive(null);
      }
      reloadChannels();
    },
    onMentioned: (e) => {
      // Someone @mentioned me (in any channel) — surface an immediate in-app ping + sound.
      setToast({ title: `${e.fromName} • ${e.channelName}`, body: e.preview || "mentioned you" });
      playNotification("mention");
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 6000);
    },
    onMeetingUpdated: (e) => {
      // The pinned card shows meetings across ALL channels — nudge it to refetch right away
      // (a reschedule/end otherwise sat stale until its 60s poll). Before the channel guard.
      setPinRefresh((n) => n + 1);
      // A meeting changed (live/ended, or the creator rescheduled it) — patch its card live.
      if (e.channelId !== activeIdRef.current) return;
      setMessages((prev) => prev.map((m) => {
        if (m.messageType !== 9 /* Meeting */ || !m.metadataJson) return m;
        try {
          const meta = JSON.parse(m.metadataJson);
          if (meta.meeting?.id !== e.meetingId) return m;
          const patch = { status: e.status };
          if (e.scheduledAt !== undefined) patch.scheduledAt = e.scheduledAt;
          if (e.title != null) patch.title = e.title;
          if (e.recurrence !== undefined) patch.recurrence = e.recurrence;
          // Agenda coverage ("📋 n/m covered") rides on the same event.
          if (e.topicsTotal != null) { patch.topicsTotal = e.topicsTotal; patch.topicsDone = e.topicsDone; }
          return { ...m, metadataJson: JSON.stringify({ ...meta, meeting: { ...meta.meeting, ...patch } }) };
        } catch {
          return m;
        }
      }));
    },
    onMeetingParticipants: (e) => {
      // Live headcount — the pinned card uses it too (enables End-meeting the moment the
      // room empties, instead of waiting for the pin's 60s poll).
      setMeetLiveCounts((prev) => ({ ...prev, [e.code]: e.count }));
      // Patch the count onto the meeting card (matched by code).
      setMessages((prev) => prev.map((m) => {
        if (m.messageType !== 9 /* Meeting */ || !m.metadataJson) return m;
        try {
          const meta = JSON.parse(m.metadataJson);
          if (meta.meeting?.code !== e.code) return m;
          return { ...m, metadataJson: JSON.stringify({ ...meta, meeting: { ...meta.meeting, participants: e.count } }) };
        } catch {
          return m;
        }
      }));
    },
    onMeetingReminder: (e) => {
      setToast({ title: e.title, body: e.body });
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 8000);
    },
    onJumpscared: (e) => {
      // /jump: flash the jumpscare fullscreen with the clown laugh, then reveal who did it.
      // (play() rejects before the tab's first user gesture — the flash still lands.)
      try { const a = jumpSound.current; if (a) { a.currentTime = 0; a.play().catch(() => {}); } } catch { /* no audio */ }
      setJumpscare(true);
      clearTimeout(jumpTimer.current);
      jumpTimer.current = setTimeout(() => setJumpscare(false), 1900); // matches the CSS animation

      try { navigator.vibrate?.([200, 60, 200]); } catch { /* unsupported */ }
      setToast({ title: `😱 ${e.fromName} jumpscared you`, body: e.channelName ? `in #${e.channelName}` : "" });
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 6000);
    },
    onNudged: (e) => {
      // MSN-style nudge: shake the whole app, play the buzzer, buzz the phone, say who did it.
      playNudgeBuzz();
      const b = document.body;
      b.classList.remove("nudged");
      void b.offsetWidth; // restart the animation if one is already playing
      b.classList.add("nudged");
      setTimeout(() => b.classList.remove("nudged"), 900);
      try { navigator.vibrate?.([90, 40, 90]); } catch { /* unsupported */ }
      setToast({ title: `👋 ${e.fromName} nudged you`, body: e.channelName ? `in #${e.channelName}` : "" });
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 6000);
    },
    onTicketDeleted: (e) => {
      // An issue/task was deleted in TMCHub → flip its chat card(s) to "removed" live.
      setDeletedRefs((prev) => {
        const n = new Set(prev);
        if (e.issueId) n.add(`issue:${e.issueId}`);
        (e.taskIds || []).forEach((t) => n.add(`task:${t}`));
        return n;
      });
    },
    onReactionChanged: (e) => {
      // Live reaction delta → update counts + "mine" in the channel message list.
      setMessages((prev) => applyReactionDelta(prev, e, user?.userId));
      setThreadMsgs((prev) => applyReactionDelta(prev, e, user?.userId));
    },
    onReconnected: () => {
      // Re-sync ALL channel unread counts (incl. the Dream Team pin) — messages that arrived while
      // the socket was down (every deploy/idle recycles it) are missed live, leaving counts stale
      // until a full reload. This self-heals them on every reconnect.
      reloadChannels();
      // Also reload the active channel so its messages aren't missing.
      refreshOpenChannel();
    },
  });

  // Re-fetch the open channel's latest page and MERGE it in (catches messages missed while the socket was down
  // or the tab was in the background) without dropping older history, scroll position or private cards.
  const refreshOpenChannel = useCallback(() => {
    const chId = activeIdRef.current;
    if (!chId) return;
    ChannelsApi.messages(chId).then((history) => {
      if (activeIdRef.current !== chId) return;
      const { keptOlder } = mergeLatest(messagesRef.current, history);
      setMessages((prev) => mergeLatest(prev, history).list);
      if (!keptOlder) setHasMore(history.length >= PAGE);
      const lastId = history.length ? history[history.length - 1].id : null;
      if (lastId) markRead(chId, lastId);
    }).catch(() => {});
  }, [markRead]);

  // --- initial channel load ----------------------------------------------
  useEffect(() => { reloadChannels(); }, [reloadChannels]);
  // Warm the on-demand panels once the app is idle, so opening one never waits on the network.
  useEffect(() => { prefetchPanels(); }, []);

  // Self-heal unread counts when the tab regains focus / becomes visible again — catches anything
  // missed while the socket was down or the tab was backgrounded (so the Dream Team pin + channel
  // rows never sit on a stale count).
  useEffect(() => {
    // Coming back to the tab fires BOTH focus and visibilitychange (and alt-tabbing fires focus a lot) — one
    // refresh per return, at most every 5 s, merged rather than replaced (refreshOpenChannel).
    let last = 0;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 5000) return;
      last = now;
      reloadChannels();
      refreshOpenChannel();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [reloadChannels, refreshOpenChannel]);

  // Keep the home-screen app-icon badge (installed PWA) AND the browser-tab indicator (title + favicon
  // dot) in sync with total unread — so you know there's a new message whether the app is installed,
  // or just open in a background tab. Clears when everything's read.
  useEffect(() => {
    const total = channels.filter((c) => !c.isMuted).reduce((n, c) => n + (c.unreadCount ?? 0), 0);
    setTabUnread(total);
    try {
      if ("setAppBadge" in navigator) {
        if (total > 0) navigator.setAppBadge(total);
        else navigator.clearAppBadge?.();
      }
    } catch { /* badging unsupported — non-fatal */ }
  }, [channels]);

  // Per-user mute toggle — optimistic, reverts on failure.
  const toggleMute = useCallback(async (channel, muted) => {
    const id = channel.id;
    setChannels((prev) => prev.map((c) => (c.id === id ? { ...c, isMuted: muted } : c)));
    setActive((a) => (a && a.id === id ? { ...a, isMuted: muted } : a));
    try { await ChannelsApi.mute(id, muted); }
    catch { setChannels((prev) => prev.map((c) => (c.id === id ? { ...c, isMuted: !muted } : c))); }
  }, []);

  // Per-user "hide status updates" preference, stored per channel on this device.
  const hideStatusKey = (id) => `tmc.hideStatus.${id}`;
  const readHideStatus = (id) => { try { return localStorage.getItem(hideStatusKey(id)) === "1"; } catch { return false; } };
  const toggleHideStatus = useCallback((channel, hide) => {
    try { if (hide) localStorage.setItem(hideStatusKey(channel.id), "1"); else localStorage.removeItem(hideStatusKey(channel.id)); } catch { /* private mode */ }
    if (activeIdRef.current === channel.id) setHideStatus(hide);
  }, []);

  // Staff: summarise this channel's unread messages (context-menu action) → show in a dialog.
  const summarizeUnread = useCallback(async (channel) => {
    setSummaryDlg({ channelName: channel.name, loading: true });
    try {
      const res = await ChannelsApi.summarizeUnread(channel.id);
      if (!res?.summary) setSummaryDlg({ channelName: channel.name, error: "Nothing to summarise — no unread messages." });
      else setSummaryDlg({ channelName: channel.name, summary: res.summary, count: res.count });
    } catch {
      setSummaryDlg({ channelName: channel.name, error: "Couldn't generate a summary right now. Please try again shortly." });
    }
  }, []);

  const openChannel = useCallback(async (channel) => {
    setHideStatus(readHideStatus(channel.id));
    // Opening the just-created channel drops its "new" pin so recency sorting resumes.
    setNewChannelId((id) => (id === channel.id ? null : id));
    // Cyberpunk: a brief "RE-ROUTING NEURAL LINK" jack-in overlay when switching channels. Use the
    // persistent last-channel ref (not activeIdRef, which is null after mobile back-navigation) so
    // mobile channel switches still trigger it — only the session's very first open is skipped.
    const prevId = lastChannelIdRef.current;
    const switching = prevId != null && prevId !== channel.id;
    lastChannelIdRef.current = channel.id;
    const cyber = document.documentElement.dataset.theme === "cyberpunk";
    if (switching && cyber) { setPendingName(channel.name); setConnecting(true); }
    setActive(channel);
    setReply(null);
    setTicketTags([]);
    setChannelTags([]);
    setTicketables([]);
    setSelectionMode(false);
    setSelectedIds(new Set());
    // "Edit my last message" (↑) is a one-shot request. Clear it on every switch: MessageList remounts per
    // channel and re-reads it on mount, and with the instant re-open (cached messages present at mount) a stale
    // request re-opened the edit box every time you came back to the channel.
    setEditSignal(null);
    const cached = msgCache.current.get(channel.id);
    setMessages(cached ? cached.messages : []); // instant re-open; the fresh page merges in below
    setTypingName(null);
    threadRootRef.current = null; setThreadRootId(null); setThreadMsgs([]);
    setNewMarkerId(null);
    setShowMedia(false); setScheduled([]); setSchedOpen(false);
    ScheduledApi.list(channel.id).then((r) => setScheduled(r || [])).catch(() => {});
    setMentionables([]);
    setHasMore(cached ? cached.hasMore : false);
    setLoadingOlder(false);
    loadingOlderRef.current = false;
    // Opening a channel clears its unread badge (locally + on the server).
    setChannels((prev) => prev.map((c) => c.id === channel.id ? { ...c, unreadCount: 0 } : c));
    try {
      const history = await ChannelsApi.messages(channel.id); // latest PAGE messages
      if (activeIdRef.current !== channel.id) return; // switched again while this was loading
      const { keptOlder } = mergeLatest(messagesRef.current, history);
      setMessages((prev) => mergeLatest(prev, history).list);
      if (!keptOlder) setHasMore(history.length >= PAGE); // a full page → there may be older messages
      // "New messages" line: walk back over others' messages until we've covered the unread count.
      const unread = channel.unreadCount ?? 0;
      if (unread > 0) {
        let seen = 0;
        for (let k = history.length - 1; k >= 0; k--) {
          if (history[k].senderUserId !== user?.userId && ++seen === unread) { setNewMarkerId(history[k].id); break; }
        }
      }
      const lastId = history.length ? history[history.length - 1].id : null;
      if (lastId) markRead(channel.id, lastId);
    } catch {
      if (!cached) setMessages([]); // keep the cached view if the network hiccups
    }
    // Hold the jack-in overlay a beat after the history lands so the reveal reads deliberate.
    if (switching && cyber) setTimeout(() => setConnecting(false), 520);
    ChannelsApi.mentionables(channel.id).then(setMentionables).catch(() => setMentionables([]));
    // Open tickets for the "#" tag picker (same list the @tickets drawer shows).
    ChannelsApi.tickets(channel.id, true).then((t) => setTicketables(t || [])).catch(() => setTicketables([]));
    // Seed the removed-card set from persisted deletions (durable; the ticket-deleted webhook
    // keeps it current). One request per channel open — no per-card existence checks.
    ChannelsApi.deletedRefs(channel.id).then((r) => setDeletedRefs((prev) => {
      const n = new Set(prev);
      (r.issueIds || []).forEach((id) => n.add(`issue:${id}`));
      (r.taskIds || []).forEach((id) => n.add(`task:${id}`));
      return n;
    })).catch(() => {});
  }, [markRead, user?.userId]);

  // --- Threads -----------------------------------------------------------------------------------
  const openThread = useCallback(async (root) => {
    threadRootRef.current = root.id;
    setThreadRootId(root.id);
    setThreadMsgs([root]);
    setThreadLoading(true);
    try {
      const r = await ThreadsApi.get(root.id);
      if (threadRootRef.current === root.id) setThreadMsgs([r.root, ...(r.replies || [])]);
    } catch { /* the root alone still shows; replies arrive live */ }
    finally { if (threadRootRef.current === root.id) setThreadLoading(false); }
  }, []);
  const closeThread = useCallback(() => { threadRootRef.current = null; setThreadRootId(null); setThreadMsgs([]); }, []);

  // --- Saved messages, reminders, scheduled messages ------------------------------------------------
  const flash = useCallback((title, body) => {
    setToast({ title, body });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(() => {
    SavedApi.list().then((rows) => setSavedMap(new Map((rows || []).map((r) =>
      [r.messageId, r.remindAt && !r.remindedAt ? r.remindAt : null])))).catch(() => {});
  }, []);
  const saveMsg = useCallback(async (m) => {
    try { await SavedApi.save(m.id); setSavedMap((prev) => new Map(prev).set(m.id, prev.get(m.id) ?? null)); flash("🔖 Saved", "Find it under Settings → Saved messages."); }
    catch (e) { flash("Couldn't save", e?.body?.error || "Please try again."); }
  }, [flash]);
  const unsaveMsg = useCallback(async (m) => {
    setSavedMap((prev) => { const n = new Map(prev); n.delete(m.id); return n; });
    try { await SavedApi.unsave(m.id); } catch { /* stays saved server-side; next load shows it */ }
  }, []);
  const remindMsg = useCallback(async (m, when) => {
    try {
      const r = await SavedApi.save(m.id, when.toISOString());
      setSavedMap((prev) => new Map(prev).set(m.id, r?.remindAt || when.toISOString()));
      flash("⏰ Reminder set", whenLabel(when.toISOString()));
    } catch (e) { flash("Couldn't set the reminder", e?.body?.error || "Please try again."); }
  }, [flash]);
  // Composer "send later". Returns false (keep the text) when it fails.
  const scheduleMsg = useCallback(async (text, mentionIds, sendAt) => {
    if (!active) return false;
    try {
      await ScheduledApi.create(active.id, text, sendAt, mentionIds?.length ? mentionIds : null);
      setScheduled(await ScheduledApi.list(active.id));
      flash("🕒 Scheduled", `Sends ${whenLabel(sendAt)}`);
      return true;
    } catch (e) { flash("Couldn't schedule", e?.body?.error || "Please try again."); return false; }
  }, [active, flash]);
  useEffect(() => {
    let live = true;
    const load = () => InboxApi.approvals().then((r) => { if (live) setApprovals(r || []); }).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    return () => { live = false; clearInterval(t); };
  }, []);
  useEffect(() => {
    let live = true;
    const load = () => UsersApi.onLeave()
      .then((rows) => { if (live) setAwayById(Object.fromEntries((rows || []).map((r) => [r.userId, r.until]))); })
      .catch(() => {});
    load();
    const t = setInterval(load, 3600000);
    return () => { live = false; clearInterval(t); };
  }, []);
  // Open a channel at a message (from a side panel / inbox). A thread reply jumps to its root.
  const goToMessage = useCallback((channelId, messageId) => {
    const c = channels.find((x) => x.id === channelId);
    if (!c) return;
    setJumpTo({ channelId, messageId });
    if (active?.id !== channelId) openChannel(c);
  }, [channels, active?.id, openChannel]);
  const cancelScheduled = useCallback(async (s) => {
    setScheduled((prev) => prev.filter((x) => x.id !== s.id));
    try { await ScheduledApi.cancel(s.id); } catch { /* already sent or gone */ }
  }, []);
  // A scheduled message of mine just went out → drop it from the strip.
  useEffect(() => {
    if (!scheduled.length) return undefined;
    const next = Math.min(...scheduled.map((s) => new Date(s.sendAt).getTime()));
    const t = setTimeout(() => setScheduled((prev) => prev.filter((s) => new Date(s.sendAt).getTime() > Date.now())), Math.max(1000, next - Date.now() + 45000));
    return () => clearTimeout(t);
  }, [scheduled]);

  // --- Push-notification deep-link → open the notification's channel ------------------
  // Cold start (app was closed): the SW opens "/?channel=<id>"; read it once, then strip the query.
  // Already open: the SW postMessages { type:"open-channel", channelId } on click. Both funnel into
  // `deepLink`, which resolves against the loaded channel list below.
  const [deepLink, setDeepLink] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const id = Number(p.get("channel"));
    return id > 0 ? { channelId: id, join: p.get("join") || null } : null;
  });
  useEffect(() => {
    if (deepLink) window.history.replaceState(null, "", window.location.pathname);
    const onSwMessage = (e) => {
      if (e.data?.type === "open-channel" && e.data.channelId)
        setDeepLink({ channelId: Number(e.data.channelId), join: e.data.join || null });
    };
    navigator.serviceWorker?.addEventListener?.("message", onSwMessage);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onSwMessage);
  }, []);
  useEffect(() => {
    if (!deepLink || channels.length === 0) return;
    const c = channels.find((x) => x.id === deepLink.channelId);
    if (c) {
      openChannel(c);                              // open the channel (mobile navigates into it)
      if (deepLink.join) setActiveMeeting({ code: deepLink.join }); // meeting reminder → join inline
    }
    setDeepLink(null);              // clear either way (unknown id → no-op, don't loop)
  }, [deepLink, channels, openChannel]);

  // --- Interactive tutorial ------------------------------------------------------------------
  // Auto-start once per browser (first login); always replayable via /tutorial.
  useEffect(() => {
    try { if (localStorage.getItem("tmc.tourDone") === "1") return; } catch { return; }
    const t = setTimeout(() => setTutorialOpen(true), 900); // let the shell settle first
    return () => clearTimeout(t);
  }, []);

  // The controller the tour uses to drive real surfaces (open a channel, the bug form, the
  // tickets drawer, seed demo cards, …). Rebuilt each render so it always sees current state.
  const tourCtrl = {
    goList: () => { if (window.matchMedia("(max-width: 899px)").matches) setActive(null); },
    openDemoChannel: () => {
      if (active) return; // already in a conversation
      const pick = channels.find((c) => !c.kind) || channels[0];
      if (pick) openChannel(pick);
    },
    armReplyDemo: () => setReply({ id: -1, senderName: "TMC", senderUserId: 0, snippet: "Welcome aboard! 👋" }),
    openBug: () => setCmdModal({ kind: "bug", initialTitle: "" }),
    openFeature: () => setCmdModal({ kind: "feature", initialTitle: "" }),
    openTickets: () => setShowTickets(true),
    openAppearance: () => setShowAppearance(true),
    openSummarize: () => setShowSummarize(true),
    setSelection: (v) => setSelectionMode(v),
    setDemoMenu: (m) => setDemoMenu(m),
    seedCards: () => setTourCards(TOUR_DEMO_CARDS),
    reset: () => {
      setDemoMenu(null); setReply(null); setCmdModal(null); setShowTickets(false);
      setShowAppearance(false); setShowSummarize(false); setSelectionMode(false); setTourCards([]);
    },
  };
  const closeTutorial = () => {
    try { localStorage.setItem("tmc.tourDone", "1"); } catch { /* private mode — fine */ }
    setTutorialOpen(false);
  };

  // Lazy-load older messages when the user scrolls to the top. Pages back from the
  // oldest persisted message currently loaded (transient command echoes have negative ids).
  const loadOlder = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id || loadingOlderRef.current) return;
    const oldest = messagesRef.current.find((m) => Number(m.id) > 0);
    if (!oldest) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const older = await ChannelsApi.messages(id, oldest.id);
      if (activeIdRef.current !== id) return; // channel switched mid-fetch
      if (older.length) setMessages((prev) => [...older, ...prev]);
      setHasMore(older.length >= PAGE);
    } catch { /* keep what we have */ }
    finally { loadingOlderRef.current = false; setLoadingOlder(false); }
  }, []);

  // --- Dream Team /status product picker ---------------------------------
  // `/status` with no product enters "pick mode": on mobile we drop to the channel list; on desktop
  // the sidebar is already visible and gets highlighted. Tapping products toggles selection (it does
  // NOT open them); "Done" fires a /status command per picked product, with the cards landing back in
  // the Dream Team channel.
  const enterStatusPick = () => {
    if (!active) return;
    setStatusPick({ fromId: active.id, selected: new Set() });
    if (window.matchMedia("(max-width: 899px)").matches) setActive(null); // reveal the list on mobile
  };
  const toggleStatusPick = (channel) => {
    setStatusPick((sp) => {
      if (!sp) return sp;
      const next = new Set(sp.selected);
      if (next.has(channel.id)) next.delete(channel.id); else next.add(channel.id);
      return { ...sp, selected: next };
    });
  };
  const cancelStatusPick = () => setStatusPick(null);
  const runStatusPick = async () => {
    const sp = statusPick;
    if (!sp) return;
    const picked = channels.filter((c) => sp.selected.has(c.id));
    setStatusPick(null);
    if (picked.length === 0) return;
    const dt = channels.find((c) => c.id === sp.fromId);
    // Return to Dream Team (needed on mobile, where we left it) so the private status cards render there.
    if (dt && (!active || active.id !== dt.id)) await openChannel(dt);
    // Stagger the /status commands so the cards arrive (and stack) in pick order.
    picked.forEach((c, i) => setTimeout(() => send(sp.fromId, `/status ${c.name}`, 0, null, null, null, null), i * 300));
  };

  function handleSend(text, mentionedIds) {
    if (!active) return;
    const trimmed = text.trim();
    // /status with no product (Dream Team) → open the product picker instead of sending.
    if (/^\/status\s*$/i.test(trimmed) && active.kind === "dreamteam") {
      enterStatusPick();
      return;
    }
    // /tutorial replays the interactive onboarding tour.
    if (/^\/tutorial\b/i.test(trimmed)) {
      setTutorialOpen(true);
      return;
    }
    // /tickets opens the drawer; /bug and /feature open the new-ticket modal (prefilled from any text).
    if (/^\/tickets\b/i.test(trimmed)) {
      setShowTickets(true);
      return;
    }
    // /poll opens the poll dialog; any trailing text becomes the initial question.
    if (/^\/poll\b/i.test(trimmed)) {
      setPollModal({ initialQuestion: trimmed.replace(/^\/poll\s*/i, "") });
      return;
    }
    // /idea opens the idea dialog; any trailing text becomes the headline.
    if (/^\/idea\b/i.test(trimmed)) {
      setIdeaModal({ initialTitle: trimmed.replace(/^\/idea\s*/i, "") });
      return;
    }
    // /create opens the chooser (Bug / Feature / Story). Picking a card opens the same
    // CommandModal that /bug, /feature, /story used to; Story is staff-only inside it.
    // Any trailing text becomes the chosen form's initial title.
    if (/^\/create\b/i.test(trimmed) && !active.kind) { // bugs/features/stories need a TMCHub product behind the channel
      setCreateMenu({ initialTitle: trimmed.replace(/^\/create\s*/i, "") });
      return;
    }
    // /campaign (staff): opens the full-screen marketing campaign builder for this product.
    if (/^\/campaign\b/i.test(trimmed) && user?.isStaff) {
      setCampaign({ channel: active });
      return;
    }
    // /summarize (all members): if no period given, pick one; otherwise send straight through.
    if (/^\/summarize\b/i.test(trimmed)) {
      const m = trimmed.match(/^\/summarize\s+(day|week|month)\b/i);
      if (m) { runSummarize(m[1].toLowerCase()); return; }
      setShowSummarize(true);
      return;
    }
    // /ask (all members; /ask and /query are aliases): with a question → optimistic "reading the
    // conversation" card that the private answer replaces. Bare → the server's one-line guide.
    const q = trimmed.match(/^\/(question|ask|query)\b\s*([\s\S]*)$/i);
    if (q) {
      const question = q[2].trim();
      if (question) runQuestion(question); else send(active.id, "/ask");
      return;
    }
    // /adreport (Reporting): bare → the period picker; with a window → send it with an optimistic
    // loading card (the public card, or a private error, replaces it when Adscope answers).
    const adr = trimmed.match(/^\/adreport\b\s*(.*)$/i);
    if (adr && active.kind === "reporting") {
      const arg = adr[1].trim();
      if (!arg) { setAdReportPick(true); return; }
      runAdReport(arg, adReportLabel(arg));
      return;
    }
    // /newproduct (Dream Team, staff): open the create-product dialog instead of sending.
    if (/^\/newproduct\b/i.test(trimmed) && active.kind === "dreamteam" && user?.isStaff) {
      setNewProduct({ open: true });
      return;
    }
    // /broadcast (Dream Team, staff): it fans out to EVERY product channel and is client-facing —
    // confirm in a dialog before sending. The server re-checks staff + channel kind.
    const bc = trimmed.match(/^\/broadcast\s+([\s\S]+)$/i);
    if (bc && active.kind === "dreamteam" && user?.isStaff) {
      const msg = bc[1].trim();
      if (!msg) return;
      setConfirmBroadcast({ text, mentionedIds, msg });
      return; // sent from the dialog's confirm
    }
    dispatchSend(text, mentionedIds);
  }

  // The actual send (also called from the /broadcast confirm dialog).
  function dispatchSend(text, mentionedIds) {
    send(active.id, text, 0, null, reply?.id ?? null, mentionedIds && mentionedIds.length ? mentionedIds : null, ticketRefsPayload(), channelRefsPayload(text));
    if (reply) setReply(null);
    if (ticketTags.length) setTicketTags([]);
    if (channelTags.length) setChannelTags([]);
  }

  // Staff whose birthday isn't stored yet get the one-time ask ("Later" snoozes it for the
  // session). Non-staff are never asked.
  useEffect(() => {
    if (!user?.isStaff || sessionStorage.getItem("tmc.bday.later")) return;
    PrefsApi.getBirthday()
      .then((r) => { if (!r?.birthDate) setShowBday(true); })
      .catch(() => { /* ask again next session */ });
  }, [user?.isStaff]);

  // Today's staff birthdays — fetched when the Dream Team channel opens (the banner lives there).
  useEffect(() => {
    setBdayNames([]);
    if (active?.kind !== "dreamteam" || !user?.isStaff) return;
    let dead = false;
    UsersApi.birthdaysToday()
      .then((r) => { if (!dead) setBdayNames(r?.names ?? []); })
      .catch(() => { /* banner just stays hidden */ });
    return () => { dead = true; };
  }, [active?.kind, active?.id, user?.isStaff]);

  // The channel's pins: polls (top-right rail, over the chat) + chat messages (the ⋯ menu's "Pinned
  // messages" panel, so they don't cover the chat). Fetched on channel open, kept live by
  // onReceive/onEdited/onDeleted above. Render drops closed/unpinned/deleted.
  useEffect(() => {
    setPinnedMsgs([]);
    if (!active?.id) return;
    let dead = false;
    PinsApi.list(active.id)
      .then((r) => { if (!dead) setPinnedMsgs(r?.messages ?? []); })
      .catch(() => { /* no pins shown */ });
    return () => { dead = true; };
  }, [active?.id]);

  const livePins = pinnedMsgs.filter((p) => p.pinnedAt && !p.isDeleted);
  const pollPins = livePins.filter((p) => { const meta = parsePollMeta(p); return meta && !meta.closed; }); // polls leave once closed
  const chatPins = livePins.filter((p) => !parsePollMeta(p)); // stay until unpinned
  // Pin a message for everyone / unpin it — MessageEdited brings the change to every client, us included.
  const pinMsg = useCallback(async (m) => { try { await PinsApi.pin(m.id); } catch { /* menu just closes */ } }, []);
  const unpinMsg = useCallback(async (m) => { try { await PinsApi.unpin(m.id); } catch { /* not allowed / gone */ } }, []);
  const canUnpin = (p) => !!user?.isStaff || p.pinnedByUserId === user?.userId || p.senderUserId === user?.userId;
  const pinnerName = (p) => p.pinnedByUserId === user?.userId ? "you"
    : p.pinnedByUserId === p.senderUserId ? p.senderName
    : (mentionables.find((x) => x.id === p.pinnedByUserId || x.userId === p.pinnedByUserId)?.name || null);

  // The open idea's LIVE message: `openIdea` holds only the id, so every MessageEdited (someone
  // else backing it or contributing) flows straight into the panel instead of leaving a snapshot.
  const openIdeaMsg = openIdea ? messages.find((x) => x.id === openIdea) : null;

  // Map armed tags to the SendMessage ticketRefs shape (null when none).
  const ticketRefsPayload = () => ticketTags.length
    ? ticketTags.map((t) => ({ id: t.id, issueId: t.issueId ?? null, taskId: t.taskId ?? null, name: t.name, isBug: !!t.isBug, type: t.type ?? null }))
    : null;
  // Dream Team "#": channels referenced on the next message → SendMessage channelRefs (null when none).
  // Resolve #channel references from the message TEXT (like @mentions are resolved), matching the
  // "#ChannelName" tokens against the other channels. Longest name first so "#Westfert Sales" wins
  // over a shorter prefix. Snapshotted into the message metadata so the chip renders + navigates.
  const channelRefsPayload = (msgText) => {
    // Only in the Dream Team ops channel does "#" reference channels; elsewhere it tags tickets.
    if (!msgText || active?.kind !== "dreamteam") return null;
    const refs = [];
    for (const c of [...channelables].sort((a, b) => (b.name || "").length - (a.name || "").length))
      if (c.name && msgText.includes(`#${c.name}`) && !refs.some((r) => r.id === c.id)) refs.push({ id: c.id, name: c.name });
    return refs.length ? refs : null;
  };
  // The other channels a Dream Team message can reference (exclude itself + any special channel).
  const channelables = channels.filter((c) => c.id !== active?.id && !c.kind)
    .map((c) => ({ id: c.id, name: c.name, clientName: c.clientName }));
  const armChannelTag = useCallback((c) => {
    if (!c?.id) return;
    setChannelTags((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, { id: c.id, name: c.name }]));
  }, []);
  const removeChannelTag = useCallback((id) => setChannelTags((prev) => prev.filter((c) => c.id !== id)), []);
  // Tap a channel-reference chip → jump to that channel.
  const openChannelById = (id) => { const c = channels.find((x) => x.id === id); if (c) openChannel(c); };

  // Esc closes the channel-header actions menu.
  useEffect(() => {
    if (!headerMenu) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setHeaderMenu(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [headerMenu]);

  // Composer ArrowUp on an empty box → edit your most recent text message in this channel.
  const editLast = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.senderUserId === user?.userId && !m.isDeleted && m.messageType === 0 && (m.content || "").trim()) {
        setEditSignal({ id: m.id, nonce: Date.now() });
        return;
      }
    }
  }, [messages, user?.userId]);

  // Arm a reply to a message (banner shows in the composer; next send carries replyToId).
  const armReply = useCallback((m) => {
    if (!m || m.isDeleted) return;
    setReply({ id: m.id, senderName: m.senderName, senderUserId: m.senderUserId, snippet: snippetOf(m) });
  }, []);

  // Arm a ticket tag from any source (# picker item, @tickets card, or a bug/feature card's
  // "reply" action). Normalises the differing shapes and dedupes by id.
  const armTicketTag = useCallback((t) => {
    if (!t) return;
    const id = t.id ?? t.taskId ?? t.issueId;
    if (!id) return;
    const issueId = t.issueId ?? null;
    let taskId = t.taskId ?? null;
    if (!issueId && !taskId) taskId = id; // # picker / @tickets items are tasks
    // Keep the card's work-item type (bug/feature/story) — isBug alone can't tell a story from a
    // feature, so the chip would render a tagged story as a feature.
    const tag = { id, issueId, taskId, name: t.name ?? t.title ?? `#${id}`, isBug: !!t.isBug, type: t.type ?? null };
    setTicketTags((prev) => (prev.some((x) => x.id === tag.id) ? prev : [...prev, tag]));
  }, []);
  const removeTicketTag = useCallback((id) => setTicketTags((prev) => prev.filter((t) => t.id !== id)), []);

  // --- staff multi-select → AI tickets -----------------------------------
  const showToast = useCallback((title, body) => {
    setToast({ title, body });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);
  // Enable web push from the header bell — ALWAYS give feedback (the old handler failed silently, so a
  // blocked/denied permission looked like "the bell does nothing").
  const handleEnableNotifications = useCallback(async () => {
    if (push.status === "granted") { showToast("Notifications are on", "You'll get alerts for @mentions and new messages."); return; }
    const outcome = await push.enable();
    const M = {
      granted: ["Notifications enabled ✓", "You'll now get alerts for @mentions and new messages."],
      denied: ["Notifications are blocked", "Your browser is blocking them. Click the lock / site-info icon in the address bar → Notifications → Allow, then reload."],
      dismissed: ["Not enabled yet", "You closed the browser prompt — click the bell again and choose Allow."],
      unconfigured: ["Not available", "Push isn't set up on the server yet."],
      unsupported: ["Not supported", "This browser can't do notifications. Try Chrome, Edge or Firefox."],
      "needs-install": ["Install first", "On iPhone: Share → Add to Home Screen, open it from there, then tap the bell again."],
      error: ["Couldn't enable notifications", push.error || "Please try again."],
    };
    const [t, b] = M[outcome] || M.error;
    showToast(t, b);
  }, [push, showToast]);

  // Nudge the user to turn notifications on, on every app open, until they're granted. Matthew (and
  // others) had them off on PC with no idea how to enable — this makes it impossible to miss.
  useEffect(() => {
    if (push.status === "granted" || push.status === "unsupported") { setNotifyNudge(false); return; }
    // Never collide with the first-run tutorial: hold the nudge until the tour is done (finished OR
    // skipped both set tmc.tourDone), then it fires right after. tutorialOpen is in deps so closing
    // the tour re-runs this and shows the nudge. Returning users (tour already done) get it as before.
    if (tutorialOpen) return;
    try { if (localStorage.getItem("tmc.tourDone") !== "1") return; } catch { return; }
    try { if (sessionStorage.getItem("tmc.notifyNudged") === "1") return; } catch { /* private mode */ }
    const t = setTimeout(() => setNotifyNudge(true), 1200); // let the boot animation settle first
    return () => clearTimeout(t);
  }, [push.status, tutorialOpen]);
  const dismissNudge = useCallback(() => {
    setNotifyNudge(false);
    try { sessionStorage.setItem("tmc.notifyNudged", "1"); } catch { /* private mode — it'll just re-show */ }
  }, []);
  // Fire /summarize with an optimistic local loading card (removed when the result arrives, or
  // flipped to an error after a timeout). The result comes back as a private CommandResult.
  const runSummarize = useCallback((period) => {
    if (!active) return;
    const id = `sum-${Date.now()}`;
    const loader = {
      id, channelId: active.id, senderUserId: user?.userId, senderName: "AI",
      messageType: 5, content: "",
      metadataJson: JSON.stringify({ kind: "summary-loading", period, title: active.name }),
      createdAt: new Date().toISOString(), reactions: [],
    };
    setMessages((prev) => [...prev, loader]);
    // If the recap hasn't replaced the loader within 45s → error. The morph keeps the loader's id,
    // so only flip it while it's STILL loading (guard on kind) — never overwrite an arrived summary.
    setTimeout(() => setMessages((prev) => prev.map((msg) => {
      if (msg.id !== id) return msg;
      let k = ""; try { k = JSON.parse(msg.metadataJson || "{}").kind; } catch { /* ignore */ }
      return k === "summary-loading" ? { ...msg, metadataJson: JSON.stringify({ kind: "summary-error", period, title: active.name }) } : msg;
    })), 45000);
    send(active.id, `/summarize ${period}`);
  }, [active, user, send]);
  // Fire /ask with an optimistic local loading card — the private answer (kind "question") or its
  // error morphs it in place; a 90s timeout flips it to an error if nothing comes back.
  const runQuestion = useCallback((question) => {
    if (!active) return;
    const id = `q-${Date.now()}`;
    const loader = {
      id, channelId: active.id, senderUserId: user?.userId, senderName: "AI",
      messageType: 5, content: "",
      metadataJson: JSON.stringify({ kind: "question-loading", question, title: active.name }),
      createdAt: new Date().toISOString(), reactions: [],
    };
    setMessages((prev) => [...prev, loader]);
    setTimeout(() => setMessages((prev) => prev.map((msg) => {
      if (msg.id !== id) return msg;
      let k = ""; try { k = JSON.parse(msg.metadataJson || "{}").kind; } catch { /* ignore */ }
      return k === "question-loading"
        ? { ...msg, metadataJson: JSON.stringify({ kind: "question-error", question, title: active.name, text: "That took too long — please ask again." }) }
        : msg;
    })), 90000);
    send(active.id, `/ask ${question}`);
  }, [active, user, send]);
  // /adreport: the window's human label for the loading card (mirrors CommandService.ResolveAdReportWindow).
  const adReportLabel = (arg) => {
    const a = (arg || "").trim().toLowerCase();
    if (a === "mtd" || a === "month") return "month to date";
    if (a.startsWith("last") || a === "previousmonth") return "last month";
    if (/\d{4}-\d{2}-\d{2}/.test(a)) return "custom";
    return "last 7 days";
  };
  // Fire /adreport with an optimistic loading card. The PUBLIC result card (or a private error) clears
  // it on arrival (onMessage); if Adscope never answers it flips to an error after 2.5 minutes.
  const runAdReport = useCallback((arg, label) => {
    if (!active) return;
    const id = `adr-${Date.now()}`;
    const loader = {
      id, channelId: active.id, senderUserId: user?.userId, senderName: "Adscope",
      messageType: 5, content: "",
      metadataJson: JSON.stringify({ kind: "adreport-loading", label }),
      createdAt: new Date().toISOString(), reactions: [],
    };
    setMessages((prev) => [...prev, loader]);
    setTimeout(() => setMessages((prev) => prev.map((msg) => {
      if (msg.id !== id) return msg;
      let k = ""; try { k = JSON.parse(msg.metadataJson || "{}").kind; } catch { /* ignore */ }
      return k === "adreport-loading"
        ? { ...msg, metadataJson: JSON.stringify({ kind: "adreport-error", message: "Adscope took too long to answer — try again in a moment." }) }
        : msg;
    })), 150000);
    send(active.id, `/adreport ${arg || ""}`.trim());
  }, [active, user, send]);
  const startSelect = useCallback((m) => {
    setSelectionMode(true);
    setSelectedIds(new Set(m && !m.isDeleted && Number(m.id) > 0 ? [m.id] : []));
  }, []);
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const cancelSelect = useCallback(() => { setSelectionMode(false); setSelectedIds(new Set()); }, []);
  const createTicketsFromSelection = useCallback(async () => {
    if (!active || selectedIds.size === 0 || selectedIds.size > MAX_SELECT || planBusy) return;
    setPlanBusy(true);
    try {
      await ChannelsApi.chatPlan(active.id, [...selectedIds]);
      cancelSelect();
      showToast("🤖 Sent to TMCHub", "Analysing the selected chat — the tickets will appear here shortly.");
    } catch (e) {
      showToast("Couldn't start", e?.body?.error || "Please try again.");
    } finally { setPlanBusy(false); }
  }, [active, selectedIds, planBusy, cancelSelect, showToast]);
  // Copy every selected message to the clipboard as a plain transcript (Name: text), in the order
  // they appear in the channel. Skips deleted rows; media-only messages note their type.
  const copySelection = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const text = messages
      .filter((m) => selectedIds.has(m.id) && !m.isDeleted)
      .map((m) => {
        const body = m.messageType === 10 ? `[file] ${(m.content || "").trim()}`
          : (m.content || "").trim()
          || (m.messageType === 2 ? "[image]" : m.messageType === 3 ? "[video]" : m.messageType === 1 ? "[voice note]" : "");
        return body ? `${m.senderName}: ${body}` : null;
      })
      .filter(Boolean)
      .join("\n");
    if (!text) { showToast("Nothing to copy", "The selected messages have no text."); return; }
    try {
      await navigator.clipboard.writeText(text);
      const n = selectedIds.size;
      showToast("Copied", `${n} message${n === 1 ? "" : "s"} copied to your clipboard.`);
      cancelSelect();
    } catch {
      showToast("Couldn't copy", "Your browser blocked clipboard access.");
    }
  }, [selectedIds, messages, showToast, cancelSelect]);

  function handleTyping() {
    if (!active) return;
    if (typingTimer.current) return; // throttle to one ping / 2s
    typing(active.id);
    typingTimer.current = setTimeout(() => { typingTimer.current = null; }, 2000);
  }

  function handleEdit(m) {
    setEditMsg(m);
    setEditDraft(m.content ?? "");
  }

  function handleDelete(m) {
    setConfirmDelete(m);
  }

  return (
    <div className={`chat ${active ? "chat--conversation" : ""}`}>
      {booting && <NeuralBoot onDone={() => { try { sessionStorage.removeItem("tmc.boot"); } catch { /* non-fatal */ } setBooting(false); }} />}
      <InstallPrompt />
      <aside className={`chat__sidebar ${statusPick ? "chat__sidebar--picking" : ""}`}>
        <header className="chat__sidebar-head">
          <span className="tmc-mark">
            <span className="tmc-mark__glyph" />
            <span className="tmc-mark__word">TMC<b>Chat</b></span>
          </span>
          {user?.isStaff && (
            <button className="iconbtn" title="New channel" onClick={() => setNewChannel(true)}><Icon name="plus" /></button>
          )}
          <div className="chat__settings">
            <button className="iconbtn" data-tour="settings" title="Settings" onClick={() => setSettingsMenu((v) => !v)}>
              {push.status !== "granted" && <span className="iconbtn__dot" />}
              <Icon name="settings" />
            </button>
            {settingsMenu && (
              <>
                <div className="settings-menu__overlay" onClick={() => setSettingsMenu(false)} />
                <div className="settings-menu" role="menu">
                  <button type="button" className="msg__menu-item" onClick={() => { setShowNotifPrefs(true); setSettingsMenu(false); }}><Icon name="bell" />Notifications</button>
                  <button type="button" className="msg__menu-item" onClick={() => { setShowAppearance(true); setSettingsMenu(false); }}><Icon name="palette" />Themes</button>
                  <button type="button" className="msg__menu-item" onClick={() => { setShowProfile(true); setSettingsMenu(false); }}><Icon name="user" />Profile</button>
                  <button type="button" className="msg__menu-item" onClick={() => { setShowSaved(true); setSettingsMenu(false); }}><span className="msg__menu-emoji">🔖</span>Saved messages</button>
                </div>
              </>
            )}
          </div>
          <button className="iconbtn" title="Sign out" onClick={signOut}><Icon name="logout" /></button>
        </header>
        <CyberStatus count={channels.length} />
        {statusPick && (
          <div className="status-pick-bar">
            <div className="status-pick-bar__hint">Tap products for a status report</div>
            <div className="status-pick-bar__actions">
              <button type="button" className="status-pick-bar__cancel" onClick={cancelStatusPick}>Cancel</button>
              <span className="status-pick-bar__count">{statusPick.selected.size} selected</span>
              <button type="button" className="status-pick-bar__go" disabled={statusPick.selected.size === 0} onClick={runStatusPick}>Done</button>
            </div>
          </div>
        )}
        {approvals.length > 0 && !statusPick && (
          <button type="button" className="inbox-entry" onClick={() => setShowApprovals(true)}>
            <span>📝 Waiting on you <b>{approvals.length}</b></span>
            <span className="inbox-entry__hint">{approvals.slice(0, 2).map((a) => a.title).join(" · ")}</span>
          </button>
        )}
        <ChannelList channels={channels} activeId={active?.id} onSelect={openChannel} onMute={toggleMute} onToggleHideStatus={toggleHideStatus} onSummarizeUnread={summarizeUnread} isStaff={user?.isStaff}
          pickMode={!!statusPick} pickedIds={statusPick?.selected} onPick={toggleStatusPick} newChannelId={newChannelId} />
        {user?.isStaff && !statusPick && channels.find((c) => c.kind === "dreamteam") && (
          <DreamTeamPin channel={channels.find((c) => c.kind === "dreamteam")} activeId={active?.id} onSelect={openChannel}
            isStaff={user?.isStaff} onSummarizeUnread={summarizeUnread} onMute={toggleMute} />
        )}
        {!statusPick && channels.find((c) => c.kind === "reporting") && (
          <ReportingPin channel={channels.find((c) => c.kind === "reporting")} activeId={active?.id} onSelect={openChannel}
            isStaff={user?.isStaff} onSummarizeUnread={summarizeUnread} onMute={toggleMute}
            onSettings={async (c) => { await openChannel(c); setShowSettings(true); }} />
        )}
      </aside>

      <section className="chat__main">
        <CyberRain />
        <AmbientScene />
        {connecting && <NeuralConnecting name={pendingName} />}
        {!active ? (
          <div className="chat__empty">
            <Welcome hint />
          </div>
        ) : (
          <>
            <header className="chat__head">
              <button className="chat__back" onClick={() => setActive(null)}><Icon name="back" /></button>
              {active.kind === "dreamteam" ? (
                <DreamTeamLogo size={36} />
              ) : active.kind === "reporting" ? (
                <ReportingLogo size={36} />
              ) : (
                <span className={`avatar avatar--sm avatar--${(Number(active.id) || 0) % 5 === 0 ? "a" : (Number(active.id) % 5 === 1 ? "b" : Number(active.id) % 5 === 2 ? "c" : Number(active.id) % 5 === 3 ? "d" : "e")}`} aria-hidden>
                  {active.iconUrl ? <img src={active.iconUrl} alt="" /> : (active.name?.[0] ?? "#")}
                </span>
              )}
              <div className="chat__title">
                <span className="name">{active.name}</span>
                <span className="chat__client">
                  {active.clientName && <span>{active.clientName}</span>}
                  <span className={`conn conn--${state}`}>{state}</span>
                </span>
              </div>
              <button className="iconbtn" title="Search chats" onClick={() => openSearch("")}><Icon name="search" /></button>
              <button ref={headerBtnRef} className={`iconbtn ${headerMenu ? "iconbtn--on" : ""}`}
                      title="Channel actions" onClick={openHeaderMenu}><Icon name="more" /></button>
              {headerMenu && createPortal(
                <>
                  <div className="hdrmenu-overlay" onClick={() => setHeaderMenu(null)} />
                  <div className="hdrmenu" style={{ top: headerMenu.top, right: headerMenu.right }}>
                    <button type="button" className="msg__menu-item"
                            onClick={() => { toggleMute(active, !active.isMuted); setHeaderMenu(null); }}>
                      <Icon name={active.isMuted ? "volume-x" : "volume"} />{active.isMuted ? "Unmute channel" : "Mute channel"}
                    </button>
                    <button type="button" className="msg__menu-item"
                            onClick={() => { startHuddle(); setHeaderMenu(null); }}>
                      <Icon name="phone" />Start a huddle
                    </button>
                    <button type="button" className="msg__menu-item"
                            onClick={() => { setShowSchedule(true); setHeaderMenu(null); }}>
                      <Icon name="calendar" />Schedule a meeting
                    </button>
                    <button type="button" className="msg__menu-item"
                            onClick={() => { setShowAgenda(true); setHeaderMenu(null); }}>
                      <Icon name="agenda" />Your meetings
                    </button>
                    <button type="button" className="msg__menu-item"
                            onClick={() => { setShowMedia(true); setHeaderMenu(null); }}>
                      <Icon name="image" />Media &amp; files
                    </button>
                    <button type="button" className="msg__menu-item"
                            onClick={() => { setShowPinned(true); setHeaderMenu(null); }}>
                      <Icon name="pin" />Pinned messages
                      {chatPins.length > 0 && <span className="msg__menu-count" aria-label={`${chatPins.length} pinned`}>{chatPins.length}</span>}
                    </button>
                    {user?.isStaff && (
                      <>
                        <div className="msg__menu-div" />
                        <button type="button" className="msg__menu-item"
                                onClick={() => { setShowSettings(true); setHeaderMenu(null); }}>
                          <Icon name="settings" />Channel settings
                        </button>
                      </>
                    )}
                  </div>
                </>,
                document.body,
              )}
            </header>

            {active.kind === "dreamteam" && user?.isStaff && <SignupsPanel />}
            {active.kind === "dreamteam" && user?.isStaff && (
              <WaitingPanel onOpen={(w) => goToMessage(w.channelId, w.threadRootId || w.messageId)} />
            )}
            {active.kind === "dreamteam" && bdayNames.length > 0 && (
              <div className="bday-banner" role="status">
                🎂 Happy birthday, <b>{bdayNames.join(" & ")}</b>! 🎉
              </div>
            )}
            {active.kind === "reporting" && (
              <div className="report-bar" role="toolbar" data-tour="reportbar">
                <span className="report-bar__hint">Affinity marketing report from Adscope — posted here for everyone, with a PDF download.</span>
                <button type="button" className="report-bar__btn" onClick={() => setAdReportPick(true)}>📈 Pull a report</button>
              </div>
            )}

            <MeetingPin liveCounts={meetLiveCounts} refreshSignal={pinRefresh}
              onJoin={({ channelId, code }) => { if (channelId) openChannelById(channelId); setActiveMeeting({ code }); }} />

            {liveHuddle && activeMeeting?.code !== liveHuddle.code && (
              <div className="meetpin-wrap">
                <div className="meetpin meetpin--live">
                  <span className="meetpin__icon"><Icon name="phone" /></span>
                  <div className="meetpin__body">
                    <strong className="meetpin__title">Huddle</strong>
                    <span className="meetpin__sub">
                      <span className="meetpin__dot" />
                      {(() => {
                        const n = meetLiveCounts[liveHuddle.code] ?? liveHuddle.participants ?? 0;
                        return n > 0 ? `${n} in the huddle` : "live now";
                      })()}
                    </span>
                  </div>
                  <button type="button" className="btn btn--primary btn--sm meetpin__join"
                    onClick={() => setActiveMeeting({ code: liveHuddle.code })}>Join</button>
                  <button type="button" className="btn btn--soft btn--sm meetpin__copy" title="Copy huddle link"
                    onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/meet/${liveHuddle.code}`).catch(() => {})}>🔗</button>
                </div>
              </div>
            )}

            {pollPins.length > 0 && (
              <div className="poll-pin-rail" aria-live="polite">
                <div className="poll-pin-rail__stack">
                  {pollPins.map((p) => <PollPin key={p.id} m={p} currentUserId={user?.userId} />)}
                </div>
              </div>
            )}

            <MessageList
              key={active.id}
              messages={tourCards.length ? [...messages, ...tourCards.map((c) => ({ ...c, channelId: active.id }))] : messages}
              currentUserId={user?.userId}
              channelId={active.id}
              channelKind={active.kind}
              mentionables={mentionables}
              hideStatusUpdates={hideStatus}
              onEditSave={(id, text) => edit(id, text)}
              onDelete={handleDelete}
              onToggleReaction={(m, e) => toggleReaction(m.id, e)}
              onReply={armReply}
              onForward={setForwardMsg}
              onPin={pinMsg}
              onUnpin={unpinMsg}
              isStaff={!!user?.isStaff}
              onOpenThread={openThread}
              newMarkerId={newMarkerId}
              savedMap={savedMap}
              onSave={saveMsg}
              onUnsave={unsaveMsg}
              onRemind={remindMsg}
              onClientRecap={user?.isStaff && !active.kind ? setRecapFor : undefined}
              awayById={awayById}
              onImageClick={setLightbox}
              onCardOpen={setOpenTicket}
              onJoinMeeting={(code) => setActiveMeeting({ code })}
              onCreateStories={user?.isStaff ? setStoriesModal : undefined}
              onIdeaOpen={(m) => setOpenIdea(m.id)}
              onOpenPdf={setOpenPdf}
              onReportOpen={setOpenReport}
              onOpenBrief={setOpenBriefPdf}
              onReviseBrief={user?.isStaff ? (brief) => setCampaign({ channel: active, reviseBriefId: brief.briefId }) : undefined}
              onTagTicket={armTicketTag}
              deletedRefs={deletedRefs}
              onLoadOlder={loadOlder}
              hasMore={hasMore}
              loadingOlder={loadingOlder}
              onPortfolioDrill={setPortfolioDrill}
              onChannelOpen={openChannelById}
              editSignal={editSignal}
              jumpToId={jumpTo && jumpTo.channelId === active.id ? jumpTo.messageId : null}
              onJumpDone={() => setJumpTo(null)}
              canSelect={user?.isStaff}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onStartSelect={startSelect}
            />

            {selectionMode ? (
              <div className="chat__selectbar" data-tour="selectbar">
                <button type="button" className="chat__selectbar-x" title="Cancel" onClick={cancelSelect}><Icon name="close" /></button>
                <span className="chat__selectbar-count">
                  {selectedIds.size} selected
                  {selectedIds.size > MAX_SELECT ? <em className="chat__selectbar-over"> · max {MAX_SELECT}</em> : null}
                </span>
                <button type="button" className="chat__selectbar-copy"
                        disabled={selectedIds.size === 0 || planBusy}
                        onClick={copySelection}>
                  <Icon name="copy" className="ic--sm" /> Copy
                </button>
                <button type="button" className="chat__selectbar-go"
                        disabled={selectedIds.size === 0 || selectedIds.size > MAX_SELECT || planBusy}
                        onClick={createTicketsFromSelection}>
                  {planBusy ? <span className="btn__spin" /> : <><Icon name="check-square" className="ic--sm" /> Create Tickets</>}
                </button>
              </div>
            ) : (
            <>
            <div className="chat__typing">
              {typingName && (
                <>
                  <span className="typing-dots"><i /><i /><i /></span>
                  {typingName} is typing…
                </>
              )}
            </div>

            {scheduled.length > 0 && (
              <div className="sched-strip">
                <button type="button" className="sched-strip__toggle" onClick={() => setSchedOpen((o) => !o)}>
                  🕒 {scheduled.length} scheduled {scheduled.length === 1 ? "message" : "messages"} · next {whenLabel(scheduled[0].sendAt)}
                  <span className="sched-strip__chev">{schedOpen ? "▾" : "▸"}</span>
                </button>
                {schedOpen && scheduled.map((x) => (
                  <div key={x.id} className="sched-strip__row">
                    <span className="sched-strip__when">{whenLabel(x.sendAt)}</span>
                    <span className="sched-strip__text">{x.content}</span>
                    <button type="button" className="sched-strip__x" title="Cancel" onClick={() => cancelScheduled(x)}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <Composer
              disabled={state !== "connected"}
              onSend={handleSend}
              onSendMedia={(type, url, caption) => { if (active) { send(active.id, caption || "", type, url, reply?.id ?? null, null, ticketRefsPayload(), channelRefsPayload(caption || "")); if (reply) setReply(null); if (ticketTags.length) setTicketTags([]); if (channelTags.length) setChannelTags([]); } }}
              onTyping={handleTyping}
              mentionables={mentionables}
              replyTarget={reply}
              onCancelReply={() => setReply(null)}
              onEditLast={editLast}
              onSchedule={scheduleMsg}
              awayById={awayById}
              ticketables={ticketables}
              ticketTags={ticketTags}
              onPickTicket={armTicketTag}
              onRemoveTicket={removeTicketTag}
              channelables={channelables}
              channelTags={channelTags}
              onPickChannel={armChannelTag}
              onRemoveChannel={removeChannelTag}
              isStaff={user?.isStaff}
              channelKind={active.kind}
              demoMenu={demoMenu}
              onSummarizeAgain={runSummarize}
            />
            </>
            )}
          </>
        )}
      </section>

      {showSchedule && active && (
        <ScheduleMeetingModal channel={active} onClose={() => setShowSchedule(false)} />
      )}

      {activeMeeting && (
        <ErrorBoundary title="The huddle window crashed" onClose={() => setActiveMeeting(null)}>
          <MeetingPanel code={activeMeeting.code} onClose={() => setActiveMeeting(null)} />
        </ErrorBoundary>
      )}

      {forwardMsg && (
        <ForwardModal message={forwardMsg} channels={channels}
          onForward={(ids) => { forward(forwardMsg.id, ids); setForwardMsg(null); }}
          onClose={() => setForwardMsg(null)} />
      )}

      {storiesModal && (
        <CreateStoriesModal message={storiesModal} channels={channels} onClose={() => setStoriesModal(null)}
          onCreated={(res) => {
            // A /ask answer is private + transient (no MessageEdited will come) — mark the created
            // proposals on our own copy so the card's button and the dialog's rows reflect them.
            const created = res?.created || [];
            if (!created.length) return;
            setMessages((prev) => prev.map((msg) => {
              if (msg.id !== storiesModal.id) return msg;
              let meta; try { meta = JSON.parse(msg.metadataJson || "{}"); } catch { return msg; }
              if (meta.kind !== "question" || !Array.isArray(meta.actions)) return msg;
              const actions = meta.actions.map((a, i) => {
                const c = created.find((x) => x.index === i);
                return c ? { ...a, storyId: c.storyId } : a;
              });
              return { ...msg, metadataJson: JSON.stringify({ ...meta, actions }) };
            }));
          }} />
      )}

      {showApprovals && (
        <ApprovalsPanel items={approvals} onClose={() => setShowApprovals(false)}
          onOpen={(a) => { setShowApprovals(false); goToMessage(a.channelId, a.messageId); }} />
      )}
      {recapFor && <RecapModal message={recapFor} onClose={() => setRecapFor(null)} />}
      {showSaved && (
        <SavedPanel onClose={() => setShowSaved(false)}
          onUnsaved={(id) => setSavedMap((prev) => { const n = new Map(prev); n.delete(id); return n; })}
          onOpen={(r) => {
            setShowSaved(false);
            const c = channels.find((x) => x.id === r.channelId);
            if (!c) return;
            setJumpTo({ channelId: c.id, messageId: r.message.threadRootId || r.messageId }); // a thread reply → its root
            if (active?.id !== c.id) openChannel(c);
          }} />
      )}
      {showMedia && active && (
        <MediaPanel channel={active} onClose={() => setShowMedia(false)} onImage={setLightbox}
          onJump={(messageId) => { setShowMedia(false); setJumpTo({ channelId: active.id, messageId }); }} />
      )}
      {showPinned && active && (
        <PinnedPanel channel={active} pins={chatPins} pinnerName={pinnerName} canUnpin={canUnpin} onUnpin={unpinMsg}
          onClose={() => setShowPinned(false)}
          onJump={(m) => { setShowPinned(false); setJumpTo({ channelId: m.channelId, messageId: m.id }); }} />
      )}
      {threadRootId && active && (
        <ThreadPanel
          messages={threadMsgs}
          loading={threadLoading}
          channelName={active.name}
          currentUserId={user?.userId}
          channelId={active.id}
          channelKind={active.kind}
          mentionables={mentionables}
          isStaff={!!user?.isStaff}
          disabled={state !== "connected"}
          onClose={closeThread}
          onSend={(text, mentionedIds) => send(active.id, text, 0, null, null, mentionedIds && mentionedIds.length ? mentionedIds : null, null, null, threadRootId)}
          onSendMedia={(type, url, caption) => send(active.id, caption || "", type, url, null, null, null, null, threadRootId)}
          onDelete={handleDelete}
          onEditSave={(id, text) => edit(id, text)}
          onToggleReaction={(m, e) => toggleReaction(m.id, e)}
          onImageClick={setLightbox}
          onForward={setForwardMsg}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog icon="🗑️" title={confirmDelete.senderUserId === user?.userId || !confirmDelete.senderUserId ? "Delete this message?" : "Remove this message?"}
          danger confirmLabel="Delete" cancelLabel="Keep it"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => { remove(confirmDelete.id); setConfirmDelete(null); }}>
          {confirmDelete.senderUserId && confirmDelete.senderUserId !== user?.userId
            ? <p>You're removing <strong>{confirmDelete.senderName}</strong>'s message for everyone in the channel. This is logged.</p>
            : <p>It will be removed for everyone in the channel.</p>}
        </ConfirmDialog>
      )}

      {confirmBroadcast && (
        <ConfirmDialog icon="📣" title="Broadcast to every product channel?" confirmLabel="Broadcast"
          onCancel={() => setConfirmBroadcast(null)}
          onConfirm={() => { dispatchSend(confirmBroadcast.text, confirmBroadcast.mentionedIds); setConfirmBroadcast(null); }}>
          <p className="confirm-modal__quote">“{confirmBroadcast.msg}”</p>
          <p>This is client-facing and lands in every product channel.</p>
        </ConfirmDialog>
      )}

      {editMsg && (
        <div className="modal-overlay is-open" onClick={() => setEditMsg(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal__body confirm-modal__body--left">
              <h3>Edit message</h3>
              <textarea className="input editmsg__text" autoFocus value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)} />
            </div>
            <div className="modal__foot">
              <button type="button" className="btn btn--soft" onClick={() => setEditMsg(null)}>Cancel</button>
              <button type="button" className="btn btn--primary"
                disabled={!editDraft.trim() || editDraft.trim() === (editMsg.content ?? "")}
                onClick={() => { edit(editMsg.id, editDraft.trim()); setEditMsg(null); }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && active && (
        <ChannelSettings
          channel={active}
          onClose={() => setShowSettings(false)}
          onChanged={reloadChannels}
        />
      )}

      {showProfile && <ProfileSettings onClose={() => setShowProfile(false)} />}

      {showTickets && active && (
        <TicketDrawer
          channel={active}
          onClose={() => setShowTickets(false)}
          onOpenTicket={setOpenTicket}
          onTagTicket={(t) => { armTicketTag(t); setShowTickets(false); }}
        />
      )}

      {search && (
        <SearchDrawer
          prefill={search.prefill}
          isStaff={!!user?.isStaff}
          channelCount={channels.filter((c) => !(user?.isStaff ? false : c.kind === "dreamteam")).length}
          onClose={() => setSearch(null)}
          onGoto={(r) => {
            setSearch(null);
            const c = channels.find((x) => x.id === r.channelId);
            if (c) { setJumpTo({ channelId: r.channelId, messageId: r.messageId }); openChannel(c); }
          }}
        />
      )}

      {showAppearance && (
        <div className="modal-overlay is-open" onClick={() => setShowAppearance(false)}>
          <div className="modal appearance-modal" data-tour="dialog" onClick={(e) => e.stopPropagation()}>
            <ThemePicker onClose={() => setShowAppearance(false)} />
          </div>
        </div>
      )}

      {showNotifPrefs && (
        <NotificationSettings push={push} onEnablePush={handleEnableNotifications}
                              onClose={() => setShowNotifPrefs(false)} />
      )}

      {showAgenda && (
        <MeetingsAgenda onOpenChannel={openChannelById}
                        onJoin={({ channelId, code }) => { if (channelId) openChannelById(channelId); setActiveMeeting({ code }); }}
                        onClose={() => setShowAgenda(false)} />
      )}

      {pollModal && active && (
        <PollModal channel={active} initialQuestion={pollModal.initialQuestion} onClose={() => setPollModal(null)} />
      )}

      {ideaModal && active && (
        <IdeaModal channel={active} channels={channels} initialTitle={ideaModal.initialTitle}
                   onClose={() => setIdeaModal(null)} />
      )}

      {/* Bound to the LIVE message (by id, not a snapshot) so contributions from other people
          appear in the open panel as their MessageEdited broadcasts land. */}
      {openIdeaMsg && (
        <IdeaPanel m={openIdeaMsg} currentUserId={user?.userId} isStaff={user?.isStaff}
                   channels={channels} currentChannel={active}
                   onClose={() => setOpenIdea(null)} />
      )}

      {showBday && (
        <BirthdayModal
          onSaved={() => setShowBday(false)}
          onClose={() => { sessionStorage.setItem("tmc.bday.later", "1"); setShowBday(false); }}
        />
      )}

      {showSummarize && active && (
        <div className="modal-overlay is-open" onClick={() => setShowSummarize(false)}>
          <div className="modal summarize-modal" data-tour="dialog" onClick={(e) => e.stopPropagation()}>
            <header className="summarize-modal__head">
              <span className="summarize-modal__icon">📝</span>
              <div>
                <h3>Summarise chat</h3>
                <p>Pick a period — the summary is private to you.</p>
              </div>
            </header>
            <div className="summarize-modal__opts">
              {[["day", "Today"], ["week", "This week"], ["month", "This month"]].map(([p, label]) => (
                <button key={p} type="button" className="summarize-modal__opt"
                        onClick={() => { runSummarize(p); setShowSummarize(false); }}>
                  <b>{label}</b><span>last {p}</span>
                </button>
              ))}
            </div>
            <button type="button" className="summarize-modal__cancel" onClick={() => setShowSummarize(false)}>Cancel</button>
          </div>
        </div>
      )}

      {adReportPick && active?.kind === "reporting" && (
        <AdReportModal onClose={() => setAdReportPick(false)} onRun={runAdReport} />
      )}
      {newChannel && (
        <NewChannelModal
          onClose={() => setNewChannel(false)}
          onCreated={async (created) => {
            // Same entrance as a new product: refresh, float it to the top for a while, and on
            // mobile drop back to the list so it's seen sliding in.
            await reloadChannels();
            if (created?.channelId) {
              setNewChannelId(created.channelId);
              clearTimeout(newChannelTimer.current);
              newChannelTimer.current = setTimeout(
                () => setNewChannelId((id) => (id === created.channelId ? null : id)), 12000);
            }
            if (window.matchMedia("(max-width: 768px)").matches) setActive(null);
          }}
        />
      )}
      {newProduct?.open && (
        <NewProductModal
          onClose={() => setNewProduct(null)}
          onCreated={async (created) => {
            // Refresh the list (the live MembershipChanged event also does this).
            await reloadChannels();
            // Float the new channel to the top of the list + play its entrance animation.
            if (created?.channelId) {
              setNewChannelId(created.channelId);
              clearTimeout(newChannelTimer.current);
              // Keep it pinned at the top for a while (or until opened, handled in openChannel),
              // then let normal recency sorting resume.
              newChannelTimer.current = setTimeout(
                () => setNewChannelId((id) => (id === created.channelId ? null : id)), 12000);
            }
            // On mobile, drop back to the channel list so the new channel is seen sliding in
            // (desktop always shows the list beside the chat, so just leave the view as-is).
            if (window.matchMedia("(max-width: 768px)").matches) setActive(null);
            setToast({ title: "Product created", body: `${created?.name} — channel added for all of TMC` });
            clearTimeout(toastTimer.current);
            toastTimer.current = setTimeout(() => setToast(null), 5000);
          }}
        />
      )}

      {createMenu && active && (
        <CreateMenuModal
          channelName={active.name}
          isStaff={!!user?.isStaff}
          onPick={(kind) => { setCmdModal({ kind, initialTitle: createMenu.initialTitle }); setCreateMenu(null); }}
          onClose={() => setCreateMenu(null)}
        />
      )}
      {cmdModal && active && (
        <CommandModal
          heading={cmdModal.kind === "bug" ? `Report a bug · ${active.name}` : cmdModal.kind === "story" ? `Create a story · ${active.name}` : `Request a feature · ${active.name}`}
          submitLabel={cmdModal.kind === "bug" ? "Log bug" : cmdModal.kind === "story" ? "Create story" : "Request feature"}
          initialTitle={cmdModal.initialTitle}
          canAssign={user?.isStaff}
          canEstimate={user?.isStaff}
          defaultAssigneeId={user?.userId}
          currentUserId={user?.userId}
          needsPlatform={cmdModal.kind === "bug" || cmdModal.kind === "feature" || cmdModal.kind === "story"}
          requireAttachment={cmdModal.kind === "feature"}
          attachLabel={cmdModal.kind === "feature" ? "Attach brief" : "Add attachment"}
          autoFocusTitle={!tutorialOpen}
          channelId={active.id}
          onSubmit={async (title, description, attachmentUrls, assignedToUserId, platformId, attachmentNames, estimateHours) => {
            const createFn = cmdModal.kind === "bug" ? CommandsApi.createBug
              : cmdModal.kind === "story" ? CommandsApi.createStory
              : CommandsApi.createFeature;
            const res = await createFn({
              channelId: active.id, title, description, attachmentUrls, assignedToUserId, platformId, attachmentNames, estimateHours,
            });
            // Show the card immediately for the submitter instead of waiting on the SignalR broadcast
            // (deduped by id here and in onReceive, so it's never doubled if the broadcast also arrives).
            const m = res?.message;
            if (m && m.channelId === activeIdRef.current)
              setMessages((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
            return res;
          }}
          onClose={() => setCmdModal(null)}
        />
      )}

      {notifyNudge && (
        <div className="modal-overlay is-open" onClick={dismissNudge}>
          <div className="modal notify-nudge" onClick={(e) => e.stopPropagation()}>
            <div className="notify-nudge__icon"><Icon name="bell" /></div>
            {push.status === "denied" ? (
              <>
                <h3 className="notify-nudge__title">Notifications are blocked</h3>
                <p className="notify-nudge__body">Your browser is blocking notifications for TMCChat, so you're missing @mentions and new messages. To turn them back on:</p>
                <ol className="notify-nudge__steps">
                  <li>Click the <b>lock / site-info icon</b> in the address bar</li>
                  <li>Set <b>Notifications</b> to <b>Allow</b></li>
                  <li>Reload the page</li>
                </ol>
                <div className="notify-nudge__actions">
                  <button type="button" className="notify-nudge__go" onClick={() => location.reload()}>Reload now</button>
                  <button type="button" className="notify-nudge__later" onClick={dismissNudge}>Not now</button>
                </div>
              </>
            ) : push.status === "needs-install" ? (
              <>
                <h3 className="notify-nudge__title">Turn on notifications</h3>
                <p className="notify-nudge__body">On iPhone, add TMCChat to your Home Screen first: tap <b>Share</b> → <b>Add to Home Screen</b>, open it from there, then enable notifications.</p>
                <div className="notify-nudge__actions">
                  <button type="button" className="notify-nudge__later" onClick={dismissNudge}>Got it</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="notify-nudge__title">Turn on notifications</h3>
                <p className="notify-nudge__body">Get alerted for <b>@mentions</b> and new messages even when the app is closed. You can turn this off anytime.</p>
                <div className="notify-nudge__actions">
                  <button type="button" className="notify-nudge__go" onClick={handleEnableNotifications}>Enable notifications</button>
                  <button type="button" className="notify-nudge__later" onClick={dismissNudge}>Not now</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {jumpscare && (
        <div className="jumpscare" aria-hidden>
          <img src="/fx/grudge.png" alt="" />
        </div>
      )}
      {toast && (
        <div className="toast-stack">
          <div className="toast" onClick={() => setToast(null)}>
            <span className="toast__icon">💬</span>
            <div className="toast__c">
              <div className="toast__title">{toast.title}</div>
              <div className="toast__body">{toast.body}</div>
            </div>
            <button className="toast__x" onClick={(e) => { e.stopPropagation(); setToast(null); }}><Icon name="close" /></button>
          </div>
        </div>
      )}

      {openTicket && active && (
        <TicketDetailModal
          channelId={active.id}
          card={openTicket}
          onClose={() => setOpenTicket(null)}
          onReplyTicket={(card) => { armTicketTag(card); setOpenTicket(null); }}
          onImageClick={setLightbox}
          isStaff={user?.isStaff}
          channels={channels}
          currentChannel={active}
        />
      )}

      {portfolioDrill && (
        <PortfolioTasksModal
          drill={portfolioDrill}
          onClose={() => setPortfolioDrill(null)}
          onOpenTicket={(card) => { setPortfolioDrill(null); setOpenTicket(card); }}
        />
      )}

      {openPdf && (
        <SignoffPdfModal doc={openPdf} onClose={() => setOpenPdf(null)} />
      )}

      {campaign && (
        <CampaignBuilder
          channel={campaign.channel}
          reviseBriefId={campaign.reviseBriefId}
          onClose={() => setCampaign(null)}
          onApplied={() => setCampaign(null)}
        />
      )}

      {openBriefPdf && (
        <CampaignPdfModal brief={openBriefPdf} onClose={() => setOpenBriefPdf(null)} />
      )}

      {openReport && (
        <ReportModal report={openReport} onClose={() => setOpenReport(null)} />
      )}

      {lightbox && (
        <div className="lightbox is-open" onClick={() => setLightbox(null)}>
          <button className="lightbox__close" onClick={() => setLightbox(null)}><Icon name="close" /></button>
          <img src={lightbox} alt="" />
        </div>
      )}

      <SummaryModal
        open={!!summaryDlg}
        title={summaryDlg?.channelName}
        loading={summaryDlg?.loading}
        error={summaryDlg?.error}
        summary={summaryDlg?.summary}
        count={summaryDlg?.count}
        onClose={() => setSummaryDlg(null)}
      />

      <Tutorial open={tutorialOpen} isStaff={user?.isStaff} ctrl={tourCtrl} onClose={closeTutorial} />
    </div>
  );
}
